import { useEffect, useState } from "react";
import { createRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Check, Copy, Share2, Store, Users } from "lucide-react";

import { rootRoute } from "./root.tsx";
import { api } from "../api/client.ts";
import { orgApi } from "../api/org-client.ts";
import { useIdentities, useMe } from "../api/queries.ts";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopInviteShare } from "./desktop/invite-share.tsx";
import { SubPage } from "../components/layout.tsx";
import { Button, Empty, ErrorBlock, Input, Spinner } from "../components/ui.tsx";
import { Card } from "@/components/ui/card";
import { shareOrigin } from "../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  邀请客户
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这是分销商最核心的动作，所以给它一整页。**
 *    以前邀请链接埋在商家端首页的最后一屏 —— 老板自己都没找到，
 *    那对一个装修师傅来说就等于不存在。
 *
 * 🔴 **必须有二维码。** 装修师傅拉客户的场景是：在工地上、在建材市场、
 *    在客户家里，掏出手机让对方扫一下。发链接要先加微信、再复制粘贴，
 *    多两步就少一半人。
 *
 * ⚠️ 二维码在**前端生成**，不走接口 —— 服务端生成要多一次往返、
 *    还要考虑缓存和并发，而这个码的内容就是一条固定的 URL，没必要。
 */

export interface InviteInfo {
  inviteCode: string;
  /** 这个链接被打开过多少次。 */
  visits: number;
  /** 从这个链接注册了多少人。 */
  registered: number;
}

function InviteShare() {
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));
  const [copied, setCopied] = useState<"link" | "code" | null>(null);
  const [qr, setQr] = useState<string>("");

  /**
   * 谁能拉客户。
   *
   * 🔴 **平台也算** —— 它是链条最上面一级，从它的链接注册的人归属平台、
   *    按平台指导零售价买。不给的话老板自己想发个链接都发不出去。
   *
   * ⚠️ 供应商不算 —— 他供货不零售，拉来的客户挂在他名下没有意义。
   */
  const orgs = (identities?.orgs ?? []).filter(
    (o) =>
      (o.type === "platform" || o.type === "dealer" || o.type === "distributor") &&
      o.status === "active",
  );
  const [pickedId, setPicked] = useState<number | null>(null);
  const org = orgs.find((o) => o.id === pickedId) ?? orgs[0];

  const invite = useQuery({
    queryKey: ["invite", org?.id],
    queryFn: () =>
      fetch("/api/merchant/invite", {
        credentials: "include",
        headers: { "X-Org-Id": String(org!.id) },
      }).then((r) => r.json() as Promise<InviteInfo>),
    enabled: Boolean(org),
  });

  const shareUrl = invite.data
    ? `${shareOrigin()}/i/${invite.data.inviteCode}`
    : "";

  /**
   * 画二维码。
   * ⚠️ 容错级别用 M（15%）—— 太低的话打印出来沾点灰就扫不出，
   *    太高会让码变密、手机镜头对不准。
   */
  useEffect(() => {
    if (!shareUrl) return;
    void QRCode.toDataURL(shareUrl, {
      width: 640,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#3d2b1f", light: "#ffffff" },
    }).then(setQr);
  }, [shareUrl]);

  async function copy(text: string, what: "link" | "code") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /** ⚠️ 非 https 下 clipboard 不可用 —— 静默失败，内容就在眼前他能自己选。 */
    }
  }

  if (mePending) {
    return (
      <SubPage title="邀请客户">
        <Spinner />
      </SubPage>
    );
  }

  if (!me || orgs.length === 0) {
    return (
      <SubPage title="邀请客户">
        <Empty
          title="你还不是商家"
          hint="邀请客户是商家功能。想开店请联系上级商家或平台。"
        />
      </SubPage>
    );
  }

  return (
    <SubPage title="邀请客户">
      {orgs.length > 1 && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto bg-background px-3 py-2.5">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] ${
                o.id === org?.id
                  ? "bg-primary font-medium text-primary-foreground"
                  : "bg-secondary text-muted-foreground"
              }`}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {invite.isPending ? (
        <Spinner />
      ) : invite.error ? (
        <ErrorBlock error={invite.error} />
      ) : (
        <div className="space-y-3 px-3 py-3 pb-6">
          {/*
            ⚠️ 排在二维码**前面**：拉来的客户下单之后要联系他付款，
               没有联系方式的话前面所有的推广动作都白费。
          */}
          {org && <ContactCard orgId={org.id} canHaveShop={org.type !== "platform"} />}

          {/* ── 二维码 ── */}
          <Card className="px-4 py-5 text-center">
            <p className="text-[15px] font-medium">{org?.name}</p>
            <p className="mt-0.5 text-[12px] text-muted-foreground">让客户扫这个码</p>

            <div className="mx-auto mt-4 w-48">
              {qr ? (
                <img
                  src={qr}
                  alt="我的邀请二维码"
                  className="w-full rounded-xl border border-border"
                />
              ) : (
                <div className="aspect-square w-full animate-pulse rounded-xl bg-secondary" />
              )}
            </div>

            {/*
              ⚠️ 明说"长按保存" —— 手机上这是唯一能把码存下来的方式，
                 不写的话大部分人只会截屏（截出来带一堆界面元素）。
            */}
            <p className="mt-3 text-[12px] text-muted-foreground">
              长按上面的码可以保存到相册，发到微信群或朋友圈
            </p>
          </Card>

          {/* ── 链接与邀请码 ── */}
          <Card className="divide-y divide-border">
            <div className="px-4 py-3">
              <p className="mb-1.5 text-[12px] text-muted-foreground">我的专属链接</p>
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-mono text-[12px]">{shareUrl}</span>
                <button
                  className="flex shrink-0 items-center gap-1 rounded-md bg-secondary px-2.5 py-1.5 text-[12px] active:opacity-70"
                  onClick={() => copy(shareUrl, "link")}
                >
                  {copied === "link" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  {copied === "link" ? "已复制" : "复制"}
                </button>
              </div>
            </div>

            <div className="px-4 py-3">
              <p className="mb-1.5 text-[12px] text-muted-foreground">
                邀请码（客户注册时手动填）
              </p>
              <div className="flex items-center gap-2">
                <span className="flex-1 font-mono text-[18px] tracking-widest">
                  {invite.data?.inviteCode}
                </span>
                <button
                  className="flex shrink-0 items-center gap-1 rounded-md bg-secondary px-2.5 py-1.5 text-[12px] active:opacity-70"
                  onClick={() => copy(invite.data!.inviteCode, "code")}
                >
                  {copied === "code" ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                  {copied === "code" ? "已复制" : "复制"}
                </button>
              </div>
            </div>
          </Card>

          {/*
            ⚠️ 有原生分享就用原生（微信/QQ/短信一次搞定），
               没有就退回复制 —— 桌面浏览器和部分安卓没有这个 API。
          */}
          {typeof navigator !== "undefined" && "share" in navigator && (
            <Button
              className="w-full"
              onClick={() =>
                void navigator
                  .share({
                    title: `${org?.name} 的建材小店`,
                    text: "我在这儿给你留了专属价，比平台价便宜",
                    url: shareUrl,
                  })
                  .catch(() => {
                    /** 用户取消分享会 reject —— 那不是错误。 */
                  })
              }
            >
              <Share2 className="size-4" />
              分享给客户
            </Button>
          )}

          {/* ── 效果 ── */}
          <Card className="flex divide-x divide-border">
            <div className="flex-1 px-4 py-3.5 text-center">
              <p className="text-[20px] font-medium tabular-nums">{invite.data?.visits ?? 0}</p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">被打开</p>
            </div>
            <div className="flex-1 px-4 py-3.5 text-center">
              <p className="text-[20px] font-medium tabular-nums text-brand-600">
                {invite.data?.registered ?? 0}
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">成了我的客户</p>
            </div>
          </Card>

          {/*
            🔴 把规则说清楚 —— 分销商最常问的两件事就是
               "他从别人那儿也扫了怎么办"和"这单算不算我的"。
          */}
          <Card className="px-4 py-3.5">
            <p className="mb-2 flex items-center gap-1.5 text-[14px] font-medium">
              <Users className="size-4 text-muted-foreground" />
              客户是怎么算成你的
            </p>
            <ul className="space-y-1.5 text-[12px] leading-relaxed text-muted-foreground">
              <li>
                · 客户从你的码或链接进来<strong className="text-foreground">下单</strong>，
                就成了你的客户
              </li>
              <li>
                · 之后他<strong className="text-foreground">不管从哪进来</strong>下单，
                都算你的
              </li>
              <li>
                · <strong className="text-foreground">先到先得</strong> ——
                已经是别人客户的人，扫你的码也不会转到你名下
              </li>
              <li>· 他看到的是你店里的价，比平台指导价便宜</li>
            </ul>
          </Card>

          {/* ⚠️ 平台没有"店铺页"（那是给经销商/分销商的），这条入口对它藏掉 */}
          {org?.type !== "platform" && (
          <Link to="/shop/$id" params={{ id: String(org!.id) }} className="block">
            <Card className="flex items-center gap-3 px-4 py-3.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50">
                <Store className="size-[18px] text-brand-600" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-medium">看看我的店长什么样</span>
                <span className="block text-[12px] text-muted-foreground">
                  客户扫码进来看到的就是这个
                </span>
              </span>
            </Card>
          </Link>
          )}
        </div>
      )}
    </SubPage>
  );
}

/**
 * 收款联系方式。
 *
 * 🔴 **这个商城不走线上支付**：买家下完单要直接联系卖家把钱付了。
 *    没填联系方式的话，他的付款页上只有一句"联系商家"却不知道联系谁 ——
 *    这单基本就黄了。所以没填时这张卡片要**主动报警**，不是安静地空着。
 *
 * ⚠️ **只放联系方式，不放收款账号。** 账号变更频繁（换卡、换收款码），
 *    存进库里过期了就是把钱打给一个作废的账户；而且落库意味着
 *    任何能读商家资料的人都能拿到他的账户。让双方直接联系最稳。
 *
 * 🔴 **平台身份不渲染这张卡。** 服务端 `/merchant/shop` 走 `anyMerchant`
 *    （只放 supplier / dealer / distributor），平台调它必然 **403** ——
 *    而平台是能进这一页的（它也要拉客户）。不判身份就等于每次都发一个
 *    注定失败的请求。判断口径跟 `/me` 的 `hasOwnShop` 一致。
 */
function ContactCard({ orgId, canHaveShop }: { orgId: number; canHaveShop: boolean }) {
  const qc = useQueryClient();
  const shop = useQuery({
    queryKey: ["merchant", "shop", orgId],
    /**
     * 🔴 **必须走统一的取数封装，不能裸 `fetch(...).then(r => r.json())`。**
     *
     *    裸 fetch 不看 `r.ok`，403 的错误体 `{code, message}` 会被当成**数据**
     *    存进查询缓存。而这个 key（`["merchant","shop",orgId]`）跟「我的」页
     *    **是同一个** —— 于是从这一页返回「我的」时，那边拿到的
     *    `shop.data` 是个错误对象，`shop.data.contactPhone` 是 undefined，
     *    `.trim()` 直接抛 `Cannot read properties of undefined`，**整页白屏**。
     *
     *    真机上就是这么炸的：平台身份进邀请页 → 点返回 → 「我的」整页没内容。
     *    ⚠️ 症状出现在**另一个页面**，所以很难从「我的」那边查出原因。
     */
    queryFn: () => orgApi.get<{ contactName: string; contactPhone: string }>("/shop", orgId),
    enabled: canHaveShop,
  });

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const save = useMutation({
    /** ⚠️ 跟上面同一个理由：走统一封装，错误一律抛出去，别混进缓存。 */
    mutationFn: () =>
      orgApi.patch("/shop", orgId, { contactName: name, contactPhone: phone }),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["merchant", "shop", orgId] });
    },
  });

  const filled = Boolean(shop.data?.contactPhone);

  return (
    <Card className="px-4 py-4">
      <div className="flex items-center justify-between">
        <p className="text-[14px] font-medium">收款联系方式</p>
        {!editing && (
          <button
            className="text-[13px] text-brand-600"
            onClick={() => {
              setName(shop.data?.contactName ?? "");
              setPhone(shop.data?.contactPhone ?? "");
              setEditing(true);
            }}
          >
            {filled ? "修改" : "去填写"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <Input label="称呼" value={name} placeholder="王师傅" onChange={(e) => setName(e.target.value)} />
          <Input
            label="电话"
            value={phone}
            placeholder="13800138000"
            inputMode="tel"
            onChange={(e) => setPhone(e.target.value)}
          />
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setEditing(false)}>
              取消
            </Button>
            <Button size="sm" className="flex-1" loading={save.isPending} onClick={() => save.mutate()}>
              保存
            </Button>
          </div>
          {save.error && <ErrorBlock error={save.error} />}
        </div>
      ) : filled ? (
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          {shop.data?.contactName && `${shop.data.contactName} · `}
          {shop.data?.contactPhone}
          <br />
          客户下单后会看到这个，联系你付款。
        </p>
      ) : (
        /*
          🔴 没填是**会直接丢单**的状态，所以用警示色主动提醒 ——
             安静地空着的话，商家永远不会意识到问题出在这里。
        */
        <p className="mt-1.5 rounded-lg bg-amber-50 px-3 py-2 text-[13px] leading-relaxed text-amber-800">
          还没填。客户下单后要联系你付款，没有电话他就只能干等 —— 这单大概率就丢了。
        </p>
      )}
    </Card>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function InviteShareRoute() {
  return useIsDesktop() ? <DesktopInviteShare /> : <InviteShare />;
}

export const inviteShareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite-share",
  component: InviteShareRoute,
});
